import { useMemo } from 'react';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { SubscriptionFeatures } from '@/lib/types';
import { useCustomPlanInfo } from './useCustomPlanNavigation';

export function useFeatureAccess() {
  const { features, hasFeature, isLoading } = useFeatureFlags();
  const { data: customPlanInfo, isLoading: isLoadingCustomPlan } = useCustomPlanInfo();
  
  // Check if user has custom plan
  const isCustomPlan = customPlanInfo?.isCustomPlan || false;
  const customFeatureKeys = customPlanInfo?.featureKeys || [];
  
  // For custom plans, check if the specific feature key is in their selection
  const hasCustomPlanFeature = (featureKey: string): boolean => {
    if (!isCustomPlan) return true; // Not custom plan, defer to normal hasFeature
    return customFeatureKeys.includes(featureKey);
  };
  
  // Memoize all feature checks to prevent unnecessary recalculations
  const featureAccess = useMemo(() => ({
    canUseCurrencyConversion: hasFeature('currency_conversion'),
    canUseMultipleCurrencies: hasFeature('multiple_currencies'),
    canUsePriceComparison: hasFeature('price_comparison'),
    canUseGroupedContracts: hasFeature('grouped_contracts'),
    canUseContractServices: hasFeature('contract_services'),
    canUseReminders: hasFeature('reminders'),
    canUsePaymentTypeSelection: hasFeature('payment_type_selection'),
    canUseDashboard: hasFeature('dashboard'),
    canUseDocumentExtraction: hasFeature('document_extraction'),
    // Custom plan specific feature checks - for custom plans, check the specific feature
    canUseContractManagement: hasFeature('contract_management' as keyof SubscriptionFeatures) || (!isCustomPlan || hasCustomPlanFeature('contract_management')),
    canUseSalesAnalyticsFeature: hasFeature('sales_analytics' as keyof SubscriptionFeatures) && (!isCustomPlan || hasCustomPlanFeature('sales_analytics')),
    canUseSalesAnalytics: hasFeature('sales_analytics' as keyof SubscriptionFeatures),
    canUseCostOptimization: hasFeature('cost_optimization' as keyof SubscriptionFeatures),
    canUseErpIntegration: hasFeature('erp_integration'),
    canUseCalendarView: hasFeature('calendar_view'),
    canUseAdvancedDashboard: hasFeature('advanced_dashboard'),
    canUseCustomFields: hasFeature('custom_fields'),
    canUseApiRead: hasFeature('api_read'),
    canUseApiWrite: hasFeature('api_write'),
    canUseDataHealth: hasFeature('data_health' as keyof SubscriptionFeatures) && (!isCustomPlan || hasCustomPlanFeature('data_health')),
    canUseGoverningDocuments: hasFeature('governing_documents' as keyof SubscriptionFeatures) && (!isCustomPlan || hasCustomPlanFeature('governing_documents')),
  }), [hasFeature, isCustomPlan, customFeatureKeys]);

  return {
    isLoading: isLoading || isLoadingCustomPlan,
    features,
    hasFeature,
    isCustomPlan,
    customFeatureKeys,
    hasCustomPlanFeature,
    ...featureAccess
  };
}
